import { useEffect, useMemo, useRef } from 'react';
import { useCartStore, type BillDiscount, type CartLineItem } from '@/stores/use-cart-store';
import { vouchersService } from '@/services/vouchers.service';
import { ApiError } from '@/lib/api-client';

/** The bill lines a voucher check sends (C03): product and net after its own discount, minor units. */
export function voucherLinesOf(
  items: Array<Pick<CartLineItem, 'productId' | 'lineSubtotal' | 'discountAmount'>>,
) {
  return items.map((i) => ({
    product_id: i.productId,
    amount: Math.max(0, i.lineSubtotal - i.discountAmount),
  }));
}

interface RecheckInput {
  billDiscount: BillDiscount | null;
  /** The priced lines (automatic promotion included), as checkout totals them. */
  items: CartLineItem[];
  subtotal: number;
  locationId: string | null | undefined;
  customerId?: string;
  applyBillDiscount: (discount: Omit<BillDiscount, 'appliedAt'>) => void;
  clearBillDiscount: () => void;
  /** Shows "Voucher <CODE> removed: no <scope> items left on the bill." */
  onDropped: (message: string) => void;
}

/**
 * C03 D13: while a voucher for some items is on the bill, check it again
 * whenever the bill's lines change (debounced). No matching item left → the
 * voucher comes off and the till says why. Otherwise the matching products
 * and the amount are refreshed, so the till's figure stays the one redeem
 * takes (a promotion that lands late or a new quantity changes it).
 */
export function useScopedVoucherRecheck(input: RecheckInput): void {
  const { billDiscount, items } = input;
  const scopedCode = billDiscount?.eligibleProductIds ? billDiscount.voucherCode : undefined;
  const linesKey = useMemo(
    () => voucherLinesOf(items).map((l) => `${l.product_id}:${l.amount}`).join(','),
    [items],
  );
  const latest = useRef(input);
  latest.current = input;

  useEffect(() => {
    if (!scopedCode) return;
    const timer = setTimeout(() => {
      const now = latest.current;
      const discount = now.billDiscount;
      if (discount?.voucherCode !== scopedCode || !now.locationId) return;
      const label = discount.scopeLabel ?? 'matching';
      const drop = () => {
        now.clearBillDiscount();
        now.onDropped(`Voucher ${scopedCode} removed: no ${label} items left on the bill.`);
      };
      if (now.items.length === 0) {
        drop();
        return;
      }
      vouchersService
        .validate({
          code: scopedCode,
          bill_amount: Math.max(1, now.subtotal),
          location_id: now.locationId,
          ...(now.customerId ? { customer_id: now.customerId } : {}),
          lines: voucherLinesOf(now.items),
        })
        .then((result) => {
          const current = useCartStore.getState().billDiscount;
          if (current?.voucherCode !== scopedCode) return;
          const { appliedAt: _appliedAt, ...rest } = current;
          now.applyBillDiscount({
            ...rest,
            value: result.discount_amount > 0 ? result.discount_amount : rest.value,
            eligibleProductIds: result.eligible_product_ids ?? [],
            scopeLabel: result.scope_label ?? label,
          });
        })
        .catch((err: unknown) => {
          if (err instanceof ApiError && err.code === 'VOUCHER_NO_MATCHING_ITEMS') drop();
        });
    }, 400);
    return () => clearTimeout(timer);
    // Re-check when a line's product or amount changes; the debounce absorbs quantity ticks.
  }, [scopedCode, linesKey]);
}
